import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { captureAcquisitionSession } from 'services/acquisitionSession';
import { markGrowthSignupStarted } from 'services/growthFunnelAnalytics';
import referralService from 'services/referralService';
import { buildLoginRedirectOptions } from 'utils/authRedirect';

export default function UniversalLoginRedirect({ signup = false, children }) {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const started = useRef(false);
  const captured = useRef(null);
  const [redirectFailed, setRedirectFailed] = useState(false);

  useEffect(() => {
    if (captured.current === location) return;
    captured.current = location;
    captureAcquisitionSession({ location });
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) referralService.storeReferralCode(ref);
  }, [location]);

  const startLogin = useCallback(async () => {
    setRedirectFailed(false);
    try {
      if (signup) markGrowthSignupStarted();

      // Signup returns here for explicit consent after Auth0 authentication.
      // Login returns to the dashboard, so legacy URLs cannot cause a loop.
      const options = buildLoginRedirectOptions(
        signup ? location : { ...location, pathname: ROUTES.HOME }
      );
      if (signup) options.authorizationParams = { screen_hint: 'signup' };
      await loginWithRedirect(options);
    } catch (_error) {
      setRedirectFailed(true);
    }
  }, [location, loginWithRedirect, signup]);

  useEffect(() => {
    if (isLoading || isAuthenticated || error || started.current) return;
    started.current = true;
    startLogin();
  }, [isLoading, isAuthenticated, error, startLogin]);

  if (isAuthenticated) {
    return (
      children || (
        <Navigate to={ROUTES.HOME + location.search + location.hash} replace />
      )
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 p-6 text-neutral-900 dark:bg-neutral-950 dark:text-white">
      {error || redirectFailed ? (
        <div className="space-y-4 text-center">
          <p role="alert">No pudimos abrir el inicio de sesión.</p>
          <button
            type="button"
            onClick={startLogin}
            className="min-h-[44px] rounded-lg bg-[#71DE80] px-5 font-medium text-neutral-950"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <p role="status">Redirigiendo a Auth0…</p>
      )}
    </main>
  );
}
